import React, { type ComponentPropsWithoutRef, createRef, type FC } from 'react';
import { type PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import type { mainVirtualAgentSidebarQuery } from '@atlassian/jira-relay/src/__generated__/mainVirtualAgentSidebarQuery.graphql';
import { useLegacySpaClickHandler } from '../../utils/index.tsx';
import messages from './messages.tsx';
import { VirtualAgentNestedNav } from './nested-nav/index.tsx';

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/project-settings
 */
export const VirtualAgentMenuItem: FC<{ projectKey: string }> = ({ projectKey }) => {
	const { formatMessage } = useIntl();
	const handleLegacySPAClick = useLegacySpaClickHandler();

	return (
		!fg('hide_vsa_endpoints') && (
			<MenuItem
				href={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent`}
				selectedOn={matchUrlPathname()}
				onClick={__SPA__ ? undefined : handleLegacySPAClick}
				ref={createRef()}
				iconAfter={
					<Lozenge appearance="new">{formatMessage(messages.virtualAgentLozengeText)}</Lozenge>
				}
			>
				{formatMessage(messages.virtualServiceAgent)}
			</MenuItem>
		)
	);
};

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/project-settings
 */
export const VirtualAgentSidebar: FC<{
	projectKey: string;
	overrides: ComponentPropsWithoutRef<typeof VirtualAgentNestedNav>['overrides'];
	shifter?: {
		groupTitle: string;
		context: string;
		weight: number;
	};
	queryReference: PreloadedQuery<mainVirtualAgentSidebarQuery>;
}> = ({ projectKey, overrides, queryReference, shifter }) => {
	const data = usePreloadedQuery<mainVirtualAgentSidebarQuery>(
		graphql`
			query mainVirtualAgentSidebarQuery(
				$cloudId: ID!
				$key: String!
				$checkForAiEnabledFF: Boolean!
			) @preloadable {
				jira @required(action: THROW) {
					jiraProjectByKey(cloudId: $cloudId, key: $key) @required(action: THROW) {
						virtualAgentConfiguration {
							__typename
							...nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentConfigurationRef
						}
					}
				}
				virtualAgent @optIn(to: ["VirtualAgent"]) {
					virtualAgentEntitlements(cloudId: $cloudId) {
						...nestedNav_navigationAppsSidebarServicedesk_VirtualAgentNestedNav_virtualAgentFeaturesRef
						isAiEnabledInAdminHub @include(if: $checkForAiEnabledFF)
						isVirtualAgentAvailable
					}
				}
			}
		`,
		queryReference,
	);

	const isAiEnabledInAdminHub = fg('require_ai_enabled_for_jsm_virtual_agent')
		? !!data.virtualAgent?.virtualAgentEntitlements?.isAiEnabledInAdminHub
		: true;
	const isSiteEntitledToUseVa =
		!!data.virtualAgent?.virtualAgentEntitlements?.isVirtualAgentAvailable && isAiEnabledInAdminHub;

	const showFullVANav =
		isSiteEntitledToUseVa ||
		data.jira.jiraProjectByKey.virtualAgentConfiguration?.__typename ===
			'VirtualAgentConfiguration';

	// If VA is not created, do not show nested nav
	if (!showFullVANav || !data.virtualAgent?.virtualAgentEntitlements) {
		return <VirtualAgentMenuItem projectKey={projectKey} />;
	}

	// Otherwise, show nested nav
	return (
		<VirtualAgentNestedNav
			projectKey={projectKey}
			overrides={overrides}
			shifter={shifter}
			virtualAgentFeaturesRef={data.virtualAgent.virtualAgentEntitlements}
			virtualAgentConfigurationRef={data.jira.jiraProjectByKey?.virtualAgentConfiguration ?? null}
		/>
	);
};
